import React from 'react';
import { View, Pressable } from 'react-native';
import { useHospitalityStore } from '../store/useHospitalityStore';
import {
  Card, CardHead, CardTitle, CardSub, CardBody,
  Chip, Avatar, Alert, Icon, Input, Field, Button, buttonFg, Txt,
  Row, Col, Spread, Screen, GuestPicker,
} from '../components';
import { useTheme } from '../theme';
import type { TicketMsg } from '../data/seed';
import { handoffTicket, getTicket, createTicket, getTickets } from '../services/support/ticket';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

/**
 * Support — ported from SK Hospitality.dc.html template lines 581-637
 * and the supportVals() view-model (lines 1594-1619). SUKI resolves tickets and
 * escalates the refund ticket to a human (Meera) with full context.
 */

// Human-facing ticket reference (#N from sk-tickets). Falls back to nothing when the
// number is absent — we never surface the internal tkt_ ULID to operators.
const ticketRef = (n?: number | null): string => (n != null ? `#${n}` : '');

function Bubble({ m, guest }: { m: TicketMsg; guest: string }) {
  const t = useTheme();
  const c = t.colors;

  // [align, label, bubbleBg, bubbleText, bubbleBorder, corner override]
  const cfg =
    m.who === 'guest'
      ? { align: 'flex-start' as const, label: guest || 'Guest', bg: c.paper2, fg: c.ink2, border: c.line, corner: { borderTopLeftRadius: 3 } }
      : m.who === 'agent'
        ? { align: 'flex-end' as const, label: 'Agent', bg: c.successSoft, fg: c.success, border: c.successLine, corner: { borderTopRightRadius: 3 } }
        : { align: 'flex-end' as const, label: 'SUKI', bg: c.accentSoft, fg: c.accentInk, border: c.accentLine, corner: { borderTopRightRadius: 3 } };

  return (
    <Col gap={3} style={{ alignItems: cfg.align, alignSelf: cfg.align, maxWidth: '82%' }}>
      <Txt size="xs" color="ink4" weight="medium">{cfg.label}</Txt>
      <View
        style={{
          backgroundColor: cfg.bg,
          borderWidth: 1,
          borderColor: cfg.border,
          paddingVertical: 10,
          paddingHorizontal: 12,
          borderRadius: 12,
          ...cfg.corner,
        }}
      >
        <Txt size="md" color={cfg.fg} style={{ lineHeight: 19 }}>{m.text}</Txt>
      </View>
    </Col>
  );
}

export default function Support() {
  const t = useTheme();
  const c = t.colors;
  const s = useHospitalityStore();
  const { tickets, activeTicketId, handoffDone } = s;
  const a = tickets.find((t) => t.id === activeTicketId) || tickets[0];
  const chip = (status: string): 'success' | 'danger' => (status === 'resolved' ? 'success' : 'danger');
  const lbl = (status: string): string => (status === 'resolved' ? 'Resolved' : 'Needs human');

  // New-ticket (manual) form state. contactId set when a CRM guest is picked.
  const [showNew, setShowNew] = React.useState(false);
  const [nSubject, setNSubject] = React.useState('');
  const [nGuest, setNGuest] = React.useState('');
  const [nPhone, setNPhone] = React.useState('');
  const [nContactId, setNContactId] = React.useState<string | undefined>(undefined);
  const [nDesc, setNDesc] = React.useState('');

  // Open a ticket → fetch its full detail (thread) from sk-tickets, merge into store.
  const handleOpenTicket = async (id: string): Promise<void> => {
    s.openTicket(id);
    try {
      const detail = await getTicket(id);
      s.upsertTicketDetail({ ...detail, by: '' });
    } catch (err) {
      handleApiError(err, 'Could not load ticket');
    }
  };

  // Log a manual (in-house) ticket → sk-tickets, then refresh the list.
  const handleCreateTicket = async (): Promise<void> => {
    if (!nSubject.trim()) { handleApiError(new Error('Subject is required'), 'Missing subject'); return; }
    try {
      const res = await createTicket({
        subject: nSubject.trim(),
        description: nDesc.trim() || undefined,
        priority: 'medium',
        type: 'complaint',
        source: 'manual',
        channel: 'In-house',
        callerName: nGuest.trim() || undefined,
        callerPhone: nPhone.trim() || undefined,
        guestRef: nContactId,
      });
      showSuccess(res, 'Ticket logged');
      s.pushEvent('warn', 'warn', `Ticket raised · ${nSubject.trim()}`);
      setShowNew(false); setNSubject(''); setNGuest(''); setNPhone(''); setNContactId(undefined); setNDesc('');
      const list = await getTickets();
      s.setTickets(list.map((t) => ({ ...t, by: '', timeline: [] })));
    } catch (err) {
      handleApiError(err, 'Could not create ticket');
    }
  };

  // Escalate to a human agent: hand the ticket off with full context, then keep the
  // existing local (optimistic) store update so the prototype UI resolves regardless.
  const handleAcceptHandoff = async (): Promise<void> => {
    s.acceptHandoff();
    if (!a) return;
    try {
      const res = await handoffTicket(a.id, {
        contextSummary: `${a.subj} — ${a.summary}`,
        conversationRef: a.id,
      });
      showSuccess(res, 'Handoff accepted');
    } catch (err) {
      handleApiError(err, 'Escalate to human failed');
    }
  };

  // Empty state — no tickets yet, but staff can still log a manual one.
  if (!a) {
    return (
      <Screen>
        <View style={{ maxWidth: 460, gap: 12 }}>
          {!showNew && (
            <View style={{ paddingVertical: 40, paddingHorizontal: 24, alignItems: 'center', gap: 12 }}>
              <Txt center color="ink3">No support tickets yet.</Txt>
              <Button variant="accent" size="sm" onPress={() => setShowNew(true)}>
                <Txt size="sm" weight="medium" color={buttonFg('accent', c)}>+ New ticket</Txt>
              </Button>
            </View>
          )}
          {showNew && (
            <Card>
              <CardHead><CardTitle>New ticket</CardTitle><CardSub>Logged as In-house</CardSub></CardHead>
              <CardBody style={{ gap: 8 }}>
                <Field label="Subject"><Input placeholder="e.g. Table 6 — drink spilled" value={nSubject} onChangeText={setNSubject} /></Field>
                <GuestPicker
                  name={nGuest}
                  phone={nPhone}
                  contactId={nContactId}
                  onChange={(v) => { setNGuest(v.name); setNPhone(v.phone); setNContactId(v.contactId); }}
                />
                <Field label="Details"><Input placeholder="What happened?" value={nDesc} onChangeText={setNDesc} /></Field>
                <Spread>
                  <Button variant="ghost" size="sm" onPress={() => setShowNew(false)}>Cancel</Button>
                  <Button variant="accent" size="sm" onPress={handleCreateTicket}>Log ticket</Button>
                </Spread>
              </CardBody>
            </Card>
          )}
        </View>
      </Screen>
    );
  }

  return (
    <Screen>
      <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
        {/* ticket list */}
        <View style={{ flex: 0.85 }}>
          <Card>
            <CardHead>
              <Row gap={8}>
                <CardTitle>Tickets</CardTitle>
                <Chip>{String(tickets.length)}</Chip>
              </Row>
              <Button variant="default" size="sm" onPress={() => setShowNew((v) => !v)}>
                <Txt size="sm" weight="medium">{showNew ? 'Close' : '+ New ticket'}</Txt>
              </Button>
            </CardHead>
            <CardBody>
              {showNew && (
                <View style={{ borderWidth: 1, borderColor: c.line, borderRadius: 10, padding: 12, marginBottom: 10, gap: 8 }}>
                  <Field label="Subject"><Input placeholder="e.g. Table 6 — drink spilled" value={nSubject} onChangeText={setNSubject} /></Field>
                  <GuestPicker
                    name={nGuest}
                    phone={nPhone}
                    contactId={nContactId}
                    onChange={(v) => { setNGuest(v.name); setNPhone(v.phone); setNContactId(v.contactId); }}
                  />
                  <Field label="Details"><Input placeholder="What happened?" value={nDesc} onChangeText={setNDesc} /></Field>
                  <Spread>
                    <Txt size="xs" color="ink4">Logged as In-house</Txt>
                    <Button variant="accent" size="sm" onPress={handleCreateTicket}>Log ticket</Button>
                  </Spread>
                </View>
              )}
              <Col gap={8}>
                {tickets.map((tk) => {
                  const act = tk.id === activeTicketId;
                  return (
                    <Pressable
                      key={tk.id}
                      onPress={() => handleOpenTicket(tk.id)}
                      style={{
                        borderRadius: 10,
                        paddingVertical: 11,
                        paddingHorizontal: 12,
                        borderWidth: 1,
                        borderColor: act ? c.accent : c.line,
                        backgroundColor: act ? c.accentSoft : c.card,
                      }}
                    >
                      <Spread gap={8} style={{ alignItems: 'flex-start' }}>
                        <View style={{ flex: 1 }}>
                          <Txt size="md" weight="medium" style={{ lineHeight: 18 }}>{tk.subj}</Txt>
                          <Txt size="xs" color="ink3" style={{ marginTop: 3 }}>{[tk.guest, tk.channel, ticketRef(tk.number)].filter(Boolean).join(' · ')}</Txt>
                        </View>
                        <Chip variant={chip(tk.status)} dot>{lbl(tk.status)}</Chip>
                      </Spread>
                    </Pressable>
                  );
                })}
              </Col>
            </CardBody>
          </Card>
        </View>

        {/* thread */}
        <View style={{ flex: 1.35 }}>
          <Card>
            <CardHead>
              <View style={{ flex: 1 }}>
                <CardTitle>{a.subj}</CardTitle>
                <CardSub>{[a.guest, a.channel, ticketRef(a.number)].filter(Boolean).join(' · ')}</CardSub>
              </View>
              <Chip variant={chip(a.status)} dot>{lbl(a.status)}</Chip>
            </CardHead>
            <CardBody>
              <Col gap={12}>
                {a.timeline.map((m, i) => <Bubble key={i} m={m} guest={a.guest} />)}

                {a.status === 'needs_human' && (
                  <View
                    style={{
                      borderWidth: 1,
                      borderColor: c.warnLine,
                      backgroundColor: c.warnSoft,
                      borderRadius: 10,
                      paddingVertical: 13,
                      paddingHorizontal: 14,
                    }}
                  >
                    <Col gap={10}>
                      <Row gap={8}>
                        <Icon name="warn" size={16} strokeWidth={1.8} color={c.warn} />
                        <Txt size="md" weight="semibold" color="warn" style={{ flex: 1 }}>
                          SUKI hit its limit — escalating with full context
                        </Txt>
                      </Row>
                      <View
                        style={{
                          backgroundColor: c.card,
                          borderWidth: 1,
                          borderColor: c.line,
                          borderRadius: 8,
                          paddingVertical: 11,
                          paddingHorizontal: 12,
                        }}
                      >
                        <Txt size="sm" color="ink2" style={{ lineHeight: 18 }}>{a.summary}</Txt>
                      </View>
                      <Spread
                        style={{
                          backgroundColor: c.card,
                          borderWidth: 1,
                          borderColor: c.line,
                          borderRadius: 8,
                          paddingVertical: 10,
                          paddingHorizontal: 12,
                        }}
                      >
                        <Row gap={10}>
                          <Avatar initials="A" size="sm" background="#4CB782" color="#fff" />
                          <View>
                            <Txt size="md" weight="medium">Agent inbox</Txt>
                            <Txt size="xs" color="ink3">context pre-loaded</Txt>
                          </View>
                        </Row>
                        <Button variant="accent" onPress={handleAcceptHandoff}>
                          <>
                            <Icon name="agent" size={15} strokeWidth={1.7} color={buttonFg('accent', c)} />
                            <Txt size="md" weight="medium" color={buttonFg('accent', c)}>Accept handoff</Txt>
                          </>
                        </Button>
                      </Spread>
                    </Col>
                  </View>
                )}

                {handoffDone && a.status === 'resolved' && (
                  <Alert variant="success">
                    <Txt size="md" color="success">
                      An agent took over with the full thread — <Txt weight="bold" color="success">no repetition for the guest</Txt>.
                    </Txt>
                  </Alert>
                )}
              </Col>
            </CardBody>
          </Card>
        </View>
      </View>
    </Screen>
  );
}
